import type { FC } from "react";
import { useMemo } from "react";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import { useListIntegrations, useListIntegrationDefinitions } from "@/queries/integration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { ServiceAccountHeaders } from "@/api";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { HostAuthenticationToken } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import type { Integration, IntegrationDefinition } from "gitpod-next-api/gitpod/v1/integration_pb";
import { ServiceAccountIntegrationCard } from "@/components/settings/organization/service-accounts/ServiceAccountIntegrationCard";

export type IntegrationWithAuth = {
    integration: PlainMessage<Integration>;
    definition?: PlainMessage<IntegrationDefinition>;
    authToken?: PlainMessage<HostAuthenticationToken>;
    isConnected: boolean;
};

export type ServiceAccountIntegrationsListProps = {
    headers?: ServiceAccountHeaders;
    serviceAccountId: string;
};

export const ServiceAccountIntegrationsList: FC<ServiceAccountIntegrationsListProps> = ({
    headers,
    serviceAccountId,
}) => {
    const { data: user } = useAuthenticatedUser();

    const { data: integrations, isPending: isPendingIntegrations } = useListIntegrations(user?.organizationId);

    const { data: integrationDefinitions, isPending: isPendingDefinitions } = useListIntegrationDefinitions();

    const { data: tokensData, isPending: isPendingTokens } = useListHostAuthenticationTokens({
        enabled: !!headers,
        headers,
    });

    const isPending = isPendingIntegrations || isPendingDefinitions || isPendingTokens;

    // Build maps for efficient lookups
    const definitionsMap = useMemo(() => {
        const map = new Map<string, PlainMessage<IntegrationDefinition>>();
        integrationDefinitions?.forEach((definition) => {
            map.set(definition.id, definition);
        });
        return map;
    }, [integrationDefinitions]);

    const authTokensMap = useMemo(() => {
        const map = new Map<string, PlainMessage<HostAuthenticationToken>>();
        tokensData?.tokens?.forEach((token) => {
            if (token.integrationId) {
                map.set(token.integrationId, token);
            }
        });
        return map;
    }, [tokensData]);

    const integrationsWithAuth: IntegrationWithAuth[] = useMemo(() => {
        if (!integrations) return [];

        return integrations.map((integration) => {
            const definition = definitionsMap.get(integration.integrationDefinitionId);
            const authToken = authTokensMap.get(integration.id);
            return {
                integration,
                definition,
                authToken,
                isConnected: !!authToken,
            };
        });
    }, [integrations, definitionsMap, authTokensMap]);

    const isEmpty = integrationsWithAuth.length === 0;

    if (!isPending && isEmpty) {
        return (
            <EmptyStateCard
                title="No integrations available"
                description="Your organization hasn't enabled any integrations yet. Contact your admin to enable integrations."
                data-testid="service-account-integrations-empty-state"
            />
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {headers &&
                integrationsWithAuth.map((integrationWithAuth) => (
                    <ServiceAccountIntegrationCard
                        key={integrationWithAuth.integration.id}
                        integrationWithAuth={integrationWithAuth}
                        headers={headers}
                        serviceAccountId={serviceAccountId}
                    />
                ))}
        </div>
    );
};
